import React from 'react';

const Logo = ({ ...props }) => {
  // Unique per instance: the logo renders in several places and SVG ids must not collide
  const p = React.useId().replace(/:/g, '');
  return (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    viewBox="0 0 160 160"
    width="42"
    height="42"
    role="img"
    aria-label="Bioinfometrics logo mark"
    {...props}
  >
    <title>Bioinfometrics</title>
    <desc>Bioinfometrics logo mark — DNA double helix encoded as a financial candlestick chart on an upward trend axis. The dual-read mark simultaneously represents molecular biology and market data intelligence.</desc>

    <defs>
      {/* ── TILE BACKGROUND GRADIENT ── */}
      <radialGradient id={`${p}bm-bg`} cx="38%" cy="32%" r="70%" gradientUnits="objectBoundingBox">
        <stop offset="0%" stopColor="#0d9674"/>
        <stop offset="45%" stopColor="#066653"/>
        <stop offset="100%" stopColor="#033d2e"/>
      </radialGradient>

      {/* ── TILE SHAPE ── */}
      <rect id={`${p}bm-tile-shape`} x="0" y="0" width="160" height="160" rx="28" ry="28"/>
      <clipPath id={`${p}bm-clip`}>
        <use href={`#${p}bm-tile-shape`}/>
      </clipPath>

      {/* ── GLASS HIGHLIGHTS ── */}
      <linearGradient id={`${p}bm-glass-hl`} x1="0%" y1="0%" x2="70%" y2="70%">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.22"/>
        <stop offset="30%" stopColor="#ffffff" stopOpacity="0.07"/>
        <stop offset="60%" stopColor="#ffffff" stopOpacity="0.00"/>
      </linearGradient>

      <radialGradient id={`${p}bm-glass-catch`} cx="28%" cy="18%" r="45%" gradientUnits="objectBoundingBox">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.14"/>
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0.00"/>
      </radialGradient>

      {/* ── BODY FILL GRADIENTS ── */}
      <linearGradient id={`${p}bm-body-purple`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#C49EF0" stopOpacity="0.92"/>
        <stop offset="40%" stopColor="#9152DC" stopOpacity="1.00"/>
        <stop offset="100%" stopColor="#6B30B0" stopOpacity="1.00"/>
      </linearGradient>
      <linearGradient id={`${p}bm-body-white`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.98"/>
        <stop offset="35%" stopColor="#ffffff" stopOpacity="0.92"/>
        <stop offset="100%" stopColor="#c8f0e1" stopOpacity="0.82"/>
      </linearGradient>
      <linearGradient id={`${p}bm-body-mid`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#C4A0F2" stopOpacity="0.90"/>
        <stop offset="100%" stopColor="#8040C8" stopOpacity="0.90"/>
      </linearGradient>

      {/* ── GLOW FILTERS ── */}
      <filter id={`${p}bm-strand-glow`} x="-25%" y="-25%" width="150%" height="150%" colorInterpolationFilters="sRGB">
        <feGaussianBlur in="SourceGraphic" stdDeviation="1.6" result="blur"/>
        <feMerge>
          <feMergeNode in="blur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>

      <filter id={`${p}bm-node-glow`} x="-80%" y="-80%" width="260%" height="260%" colorInterpolationFilters="sRGB">
        <feGaussianBlur in="SourceGraphic" stdDeviation="2.0" result="blur"/>
        <feMerge>
          <feMergeNode in="blur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>

      <filter id={`${p}bm-body-glow`} x="-15%" y="-8%" width="130%" height="116%" colorInterpolationFilters="sRGB">
        <feGaussianBlur in="SourceGraphic" stdDeviation="1.0" result="blur"/>
        <feMerge>
          <feMergeNode in="blur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>

      <filter id={`${p}bm-purple-glow`} x="-80%" y="-80%" width="260%" height="260%" colorInterpolationFilters="sRGB">
        <feGaussianBlur in="SourceGraphic" stdDeviation="1.8" result="blur"/>
        <feMerge>
          <feMergeNode in="blur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>

    {/* LAYER 1 — TILE BACKGROUND */}
    <rect x="0" y="0" width="160" height="160" rx="28" ry="28" fill={`url(#${p}bm-bg)`}/>

    {/* LAYER 2 — MARK CONTENT */}
    <g clipPath={`url(#${p}bm-clip)`}>
      <line x1="10" y1="128" x2="150" y2="18" stroke="rgba(255,255,255,0.09)" strokeWidth="0.9" strokeDasharray="3.5 5" strokeLinecap="round"/>

      <rect x="20" y="89" width="6" height="49" rx="1.6" ry="1.6" fill={`url(#${p}bm-body-purple)`} filter={`url(#${p}bm-body-glow)`}/>
      <rect x="34" y="86" width="6" height="42" rx="1.6" ry="1.6" fill={`url(#${p}bm-body-purple)`} filter={`url(#${p}bm-body-glow)`}/>
      <rect x="67" y="59" width="6" height="45" rx="1.6" ry="1.6" fill={`url(#${p}bm-body-mid)`} filter={`url(#${p}bm-body-glow)`}/>
      <rect x="77" y="47" width="6" height="52" rx="2.0" ry="2.0" fill={`url(#${p}bm-body-white)`} filter={`url(#${p}bm-body-glow)`}/>
      <rect x="87" y="43" width="6" height="45" rx="1.6" ry="1.6" fill={`url(#${p}bm-body-mid)`} filter={`url(#${p}bm-body-glow)`}/>
      <rect x="120" y="16" width="6" height="46" rx="1.6" ry="1.6" fill={`url(#${p}bm-body-purple)`} filter={`url(#${p}bm-body-glow)`}/>
      <rect x="134" y="7" width="6" height="42" rx="1.6" ry="1.6" fill={`url(#${p}bm-body-mid)`} filter={`url(#${p}bm-body-glow)`}/>

      <path d="M 10,128 C 15,114 19,84 23,89 C 27,94 33,90 37,86 C 41,83 46,94 50,97 C 56,101 64,107 70,104 C 74,102 77,101 80,99 C 83,97 87,91 90,88 C 97,82 107,52 110,49 C 113,46 119,12 123,16 C 127,20 133,6 137,7 C 141,8 147,14 150,18" fill="none" stroke="rgba(255,255,255,0.38)" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" filter={`url(#${p}bm-strand-glow)`}/>
      <path d="M 10,128 C 15,132 19,140 23,138 C 27,136 33,126 37,128 C 41,130 46,103 50,97 C 56,89 65,63 70,59 C 73,57 77,49 80,47 C 83,45 87,42 90,43 C 97,45 107,52 110,49 C 113,46 119,66 123,62 C 127,58 133,46 137,49 C 141,52 147,22 150,18" fill="none" stroke="white" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" filter={`url(#${p}bm-strand-glow)`}/>

      <circle cx="23" cy="89" r="2.8" fill="rgba(255,255,255,0.48)"/>
      <circle cx="37" cy="86" r="2.4" fill="rgba(255,255,255,0.40)"/>
      <circle cx="70" cy="104" r="2.4" fill="rgba(255,255,255,0.36)"/>
      <circle cx="80" cy="99" r="2.8" fill="rgba(255,255,255,0.44)"/>
      <circle cx="90" cy="88" r="2.4" fill="rgba(255,255,255,0.36)"/>
      <circle cx="123" cy="16" r="2.8" fill="rgba(255,255,255,0.50)"/>
      <circle cx="137" cy="7" r="2.4" fill="rgba(255,255,255,0.44)"/>

      <circle cx="37" cy="128" r="3.4" fill="#A97EE8"/>
      <circle cx="70" cy="59" r="3.2" fill="rgba(255,255,255,0.72)"/>
      <circle cx="90" cy="43" r="3.2" fill="rgba(255,255,255,0.72)"/>
      <circle cx="137" cy="49" r="3.4" fill="#A97EE8"/>

      <circle cx="23" cy="138" r="4.2" fill="#9152DC" filter={`url(#${p}bm-purple-glow)`}/>
      <circle cx="123" cy="62" r="4.2" fill="#9152DC" filter={`url(#${p}bm-purple-glow)`}/>

      <circle cx="10" cy="128" r="5.5" fill="white" filter={`url(#${p}bm-node-glow)`}/>
      <circle cx="50" cy="97" r="5.5" fill="white" filter={`url(#${p}bm-node-glow)`}/>
      <circle cx="110" cy="49" r="5.5" fill="white" filter={`url(#${p}bm-node-glow)`}/>
      <circle cx="150" cy="18" r="5.5" fill="white" filter={`url(#${p}bm-node-glow)`}/>
      <circle cx="80" cy="47" r="4.6" fill="white" filter={`url(#${p}bm-node-glow)`}/>
    </g>

    {/* LAYER 3 — GLASS EFFECTS */}
    <rect x="0" y="0" width="160" height="160" rx="28" ry="28" fill={`url(#${p}bm-glass-hl)`} pointerEvents="none"/>
    <rect x="0" y="0" width="160" height="160" rx="28" ry="28" fill={`url(#${p}bm-glass-catch)`} pointerEvents="none"/>
    <rect x="0.75" y="0.75" width="158.5" height="158.5" rx="27.5" ry="27.5" fill="none" stroke="rgba(255,255,255,0.20)" strokeWidth="1.5" pointerEvents="none"/>
    <rect x="1.5" y="1.5" width="157" height="157" rx="27" ry="27" fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="1" pointerEvents="none"/>
  </svg>
  );
};

export default Logo;
